import { api } from '@/lib/api'
import type {
  ArAgingReport,
  ArSummary,
  CreditNote,
  CreditNotePayload,
  Customer,
  CustomerPayload,
  InvoiceDetail,
  InvoiceFilters,
  InvoiceListItem,
  InvoicePayload,
  InvoicingSettings,
  InvoicingSettingsPatch,
  PendingApprovalResponse,
  PortalSnapshot,
  PortalTokenIssueResult,
  PublicInvoice,
  RefundPayload,
  WriteOffPayload,
} from './types'

export const invoicingApi = {
  // Customers
  listCustomers: (params?: { search?: string; isActive?: boolean }) => {
    const qs = new URLSearchParams()
    if (params?.search) qs.set('search', params.search)
    if (params?.isActive !== undefined) qs.set('isActive', String(params.isActive))
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return api.get<{ customers: Customer[] }>(`/api/invoicing/customers${suffix}`)
  },
  getCustomer: (id: number) =>
    api.get<{ customer: Customer; ar_balance: number }>(`/api/invoicing/customers/${id}`),
  createCustomer: (body: CustomerPayload) =>
    api.post<{ customer: Customer }>('/api/invoicing/customers', body),
  updateCustomer: (id: number, body: CustomerPayload) =>
    api.put<{ customer: Customer }>(`/api/invoicing/customers/${id}`, body),
  deactivateCustomer: (id: number) =>
    api.del<{ customer: Customer }>(`/api/invoicing/customers/${id}`),
  reactivateCustomer: (id: number) =>
    api.post<{ customer: Customer }>(`/api/invoicing/customers/${id}/reactivate`),

  // Invoices
  listInvoices: (filters?: InvoiceFilters) => {
    const qs = new URLSearchParams()
    if (filters?.status) qs.set('status', filters.status)
    if (filters?.customer_id) qs.set('customer_id', String(filters.customer_id))
    if (filters?.date_from) qs.set('date_from', filters.date_from)
    if (filters?.date_to) qs.set('date_to', filters.date_to)
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return api.get<{ invoices: InvoiceListItem[] }>(`/api/invoicing/invoices${suffix}`)
  },
  getInvoice: (id: number) =>
    api.get<{ invoice: InvoiceDetail }>(`/api/invoicing/invoices/${id}`),
  nextNumber: () =>
    api.get<{ invoice_number: string }>('/api/invoicing/invoices/next-number'),
  nextPoNumber: (customerId: number | undefined) =>
    api.get<{ po_number: string | null }>(
      '/api/invoicing/invoices/next-po-number',
      { query: customerId ? { customer_id: customerId } : {} },
    ),
  uploadInvoiceLogo: async (file: File): Promise<string> => {
    const fd = new FormData()
    fd.append('image', file)
    const { tokenManager } = await import('@/lib/tokenManager')
    const { ApiError } = await import('@/lib/api')
    const doUpload = async () => {
      const token = tokenManager.get()
      return fetch('/api/invoicing/invoices/upload-logo', {
        method: 'POST',
        credentials: 'same-origin',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      })
    }
    let res = await doUpload()
    if (res.status === 401) {
      if (await tokenManager.refreshNow()) res = await doUpload()
    }
    if (!res.ok) {
      let body: unknown
      try { body = await res.json() } catch { /* ignore */ }
      throw new ApiError(`Logo upload failed (${res.status})`, res.status, body)
    }
    const { key } = (await res.json()) as { key: string }
    return key
  },
  createInvoice: (body: InvoicePayload) =>
    api.post<{ invoice: InvoiceDetail }>('/api/invoicing/invoices', body),
  updateInvoice: (id: number, body: Partial<InvoicePayload>) =>
    api.put<{ invoice: InvoiceDetail }>(`/api/invoicing/invoices/${id}`, body),
  postInvoice: (id: number) =>
    api.post<{ invoice: InvoiceDetail } | PendingApprovalResponse>(`/api/invoicing/invoices/${id}/post`),
  sendInvoice: (id: number) =>
    api.post<{ invoice: InvoiceDetail }>(`/api/invoicing/invoices/${id}/send`),
  voidInvoice: (id: number, reason: string) =>
    api.post<{ invoice: InvoiceDetail }>(`/api/invoicing/invoices/${id}/void`, { reason }),
  deleteDraftInvoice: (id: number) =>
    api.del<{ deleted: boolean }>(`/api/invoicing/invoices/${id}`),
  remind: (id: number) =>
    api.post<{ reminder_number: number; subject: string; to: string }>(
      `/api/invoicing/invoices/${id}/remind`,
    ),
  markPaid: (id: number, body: { amount: number; received_at?: string; method?: string; note?: string }) =>
    api.post<{
      invoice: InvoiceDetail
      paymentId: number
      invoiceId: number
      finalStatus: 'paid' | 'partially_paid' | string
    }>(`/api/invoicing/invoices/${id}/mark-paid`, body),

  // Phase 2c - credit notes, refunds, write-offs
  listCreditNotes: (params?: { customer_id?: number; invoice_id?: number; status?: string }) => {
    const qs = new URLSearchParams()
    if (params?.customer_id) qs.set('customer_id', String(params.customer_id))
    if (params?.invoice_id) qs.set('invoice_id', String(params.invoice_id))
    if (params?.status) qs.set('status', params.status)
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return api.get<{ credit_notes: CreditNote[] }>(`/api/invoicing/credit-notes${suffix}`)
  },
  issueCreditNote: (body: CreditNotePayload) =>
    api.post<{ credit_note: CreditNote } | PendingApprovalResponse>('/api/invoicing/credit-notes', body),
  issueRefund: (body: RefundPayload) =>
    api.post<{ refund: { id: number; invoice_id: number; amount: string } } | PendingApprovalResponse>('/api/invoicing/refunds', body),
  writeOffInvoice: (body: WriteOffPayload) =>
    api.post<{ write_off: { id: number; invoice_id: number; amount: string } } | PendingApprovalResponse>('/api/invoicing/write-offs', body),

  // Recurring invoice templates (Phase 2b follow-up)
  listRecurring: () =>
    api.get<{ templates: import('./types').RecurringInvoiceTemplate[] }>('/api/invoicing/recurring'),
  createRecurring: (body: import('./types').RecurringInvoiceTemplatePayload) =>
    api.post<{ template: import('./types').RecurringInvoiceTemplate }>('/api/invoicing/recurring', body),
  updateRecurring: (id: number, body: Partial<import('./types').RecurringInvoiceTemplatePayload>) =>
    api.put<{ template: import('./types').RecurringInvoiceTemplate }>(`/api/invoicing/recurring/${id}`, body),
  deleteRecurring: (id: number) =>
    api.del<{ deleted: boolean }>(`/api/invoicing/recurring/${id}`),

  // AR stats - dashboard widget + platform admin tile.
  // Optional from/to window: when present the dashboard's "paid in
  // period" + recent activity reflect the selected period filter.
  arSummary: (window?: { from: string; to: string }) => {
    const qs = new URLSearchParams()
    if (window?.from && window?.to) {
      qs.set('from', window.from)
      qs.set('to', window.to)
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return api.get<ArSummary>(`/api/invoicing/stats/ar-summary${suffix}`)
  },

  // Phase 2d - AR aging report
  arAging: (params?: { as_of?: string }) => {
    const qs = new URLSearchParams()
    if (params?.as_of) qs.set('as_of', params.as_of)
    const suffix = qs.toString() ? `?${qs.toString()}` : ''
    return api.get<{ report: ArAgingReport }>(`/api/invoicing/reports/ar-aging${suffix}`)
  },

  // Phase 2d - customer portal token lifecycle (tenant side)
  issuePortalToken: (customerId: number) =>
    api.post<PortalTokenIssueResult>(`/api/invoicing/customers/${customerId}/portal-token`),
  revokePortalToken: (customerId: number) =>
    api.del<{ revoked: boolean }>(`/api/invoicing/customers/${customerId}/portal-token`),

  // Phase 2d - public portal fetch (no auth)
  fetchPortalSnapshot: async (token: string, timeoutMs = 15000): Promise<PortalSnapshot> => {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), timeoutMs)
    try {
      const res = await fetch(`/api/pay/portal/${encodeURIComponent(token)}`, {
        credentials: 'omit',
        signal: ctrl.signal,
      })
      if (!res.ok) {
        const body = await res.json().catch(() => null)
        throw new Error(body?.error || `Failed to load portal (${res.status})`)
      }
      return (await res.json()) as PortalSnapshot
    } catch (e) {
      if ((e as Error).name === 'AbortError') {
        throw new Error('The request timed out. Please retry.')
      }
      throw e
    } finally {
      clearTimeout(timer)
    }
  },

  // Tenant invoicing settings - Company page "Invoicing defaults" card.
  getSettings: () => api.get<{ settings: InvoicingSettings }>('/api/invoicing/settings'),
  patchSettings: (patch: InvoicingSettingsPatch) =>
    api.patch<{ settings: InvoicingSettings }>('/api/invoicing/settings', patch),

  // Public (no auth) - used by HostedInvoicePaymentPage via raw fetch.
  // Wrapped with AbortController so a cold ECS task / stalled fetch fails
  // fast instead of spinning the spinner forever (Gmail prefetch + cold
  // starts were leaving the first tab hung).
  fetchPublicInvoice: async (token: string, timeoutMs = 15000): Promise<PublicInvoice> => {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), timeoutMs)
    try {
      const res = await fetch(`/api/pay/invoice/${encodeURIComponent(token)}`, {
        credentials: 'omit',
        signal: ctrl.signal,
      })
      if (!res.ok) {
        const body = await res.json().catch(() => null)
        throw new Error(body?.error || `Failed to load invoice (${res.status})`)
      }
      const body = await res.json()
      return body.invoice as PublicInvoice
    } catch (e) {
      if ((e as Error).name === 'AbortError') {
        throw new Error('The request timed out. Please retry.')
      }
      throw e
    } finally {
      clearTimeout(timer)
    }
  },
  pingPublicViewed: async (token: string) => {
    try {
      await fetch(`/api/pay/invoice/${encodeURIComponent(token)}/viewed`, {
        method: 'POST',
        credentials: 'omit',
        keepalive: true,
      })
    } catch { /* fire-and-forget */ }
  },
  createPublicCheckout: async (token: string): Promise<{ url: string }> => {
    const res = await fetch(`/api/pay/invoice/${encodeURIComponent(token)}/checkout`, {
      method: 'POST',
      credentials: 'omit',
    })
    if (!res.ok) {
      const body = await res.json().catch(() => null)
      throw new Error(body?.error || 'Failed to create checkout session')
    }
    return res.json()
  },

  // ── Stripe Connect (Feature 2) ──
  getStripeConnectStatus: () =>
    api.get<{
      mode: 'connect_express' | 'bokapsys_managed' | null
      stripeAccountId: string | null
      country: string | null
      chargesEnabled: boolean
      payoutsEnabled: boolean
      detailsSubmitted: boolean
      syncedAt: string | null
    }>('/api/invoicing/stripe-connect/status'),
  setStripeConnectMode: (mode: 'connect_express' | 'bokapsys_managed') =>
    api.post<{
      ok: true
      mode: string
      // Hint from the server about the next UI step. For
      // connect_express: 'onboard_required' | 'resume_onboarding' | 'ready'.
      // For bokapsys_managed: 'configure_payout_account'.
      nextAction: 'onboard_required' | 'resume_onboarding' | 'ready' | 'configure_payout_account' | null
    }>('/api/invoicing/stripe-connect/mode', { mode }),
  startStripeConnectOnboarding: (country: string) =>
    api.post<{ stripeAccountId: string; url: string; expiresAt: string }>(
      '/api/invoicing/stripe-connect/onboard',
      { country },
    ),
  refreshStripeConnectLink: () =>
    api.post<{ url: string; expiresAt: string }>('/api/invoicing/stripe-connect/refresh', {}),
  syncStripeConnect: () =>
    api.get<{
      stripeAccountId: string | null
      chargesEnabled: boolean
      payoutsEnabled: boolean
      detailsSubmitted: boolean
      country: string | null
    }>('/api/invoicing/stripe-connect/sync'),
  openStripeDashboard: () =>
    api.post<{ url: string }>('/api/invoicing/stripe-connect/dashboard', {}),
  disconnectStripe: () =>
    api.post<{ ok: true }>('/api/invoicing/stripe-connect/disconnect', {}),
  listStripeConnectCountries: () =>
    api.get<{ countries: string[] }>('/api/invoicing/stripe-connect/supported-countries'),
}
